import React, { useState, useEffect } from 'react';
import chemicalService from '../../../services/chemicalService';

const DashboardTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await chemicalService.getDashboard();
      setData(res.data);
    } catch (err) {
      console.error('Error fetching chemical dashboard:', err);
      setError(err.response?.data?.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 20px' }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
        <h3 style={{ color: '#7f8c8d' }}>Loading dashboard...</h3>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 20px' }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
        <h3 style={{ color: '#e74c3c', marginBottom: '12px' }}>Failed to load dashboard</h3>
        <p style={{ color: '#7f8c8d', marginBottom: '24px' }}>{error}</p>
        <button onClick={fetchDashboard} style={styles.retryButton}>🔄 Retry</button>
      </div>
    );
  }

  const kpis = data?.kpis || {};
  const alerts = data?.alerts || [];
  const productionThisWeek = data?.production_this_week || [];
  const recentActivity = data?.recent_activity || [];

  return (
    <div style={{ padding: '24px 30px 40px' }}>

      {/* ─── KPI Cards ─────────────────────────────── */}
      <div style={styles.kpiGrid}>
        <div style={{ ...styles.kpiCard, borderLeft: '4px solid #3498db' }}>
          <div style={styles.kpiLabel}>Stock Value</div>
          <div style={{ ...styles.kpiValue, color: '#2c3e50' }}>
            {formatLakh(kpis.stock_value)}
          </div>
          <div style={styles.kpiSub}>Total inventory value</div>
        </div>

        <div style={{ ...styles.kpiCard, borderLeft: '4px solid #27ae60' }}>
          <div style={styles.kpiLabel}>This Month's Sales</div>
          <div style={{ ...styles.kpiValue, color: '#27ae60' }}>
            {formatLakh(kpis.this_month_sales)}
          </div>
          <div style={styles.kpiSub}>
            {kpis.this_month_sales_count || 0} invoices
          </div>
        </div>

        <div style={{ ...styles.kpiCard, borderLeft: '4px solid #f39c12' }}>
          <div style={styles.kpiLabel}>This Month's Purchases</div>
          <div style={{ ...styles.kpiValue, color: '#f39c12' }}>
            {formatLakh(kpis.this_month_purchases)}
          </div>
          <div style={styles.kpiSub}>
            {kpis.this_month_purchases_count || 0} entries
          </div>
        </div>

        <div style={{ ...styles.kpiCard, borderLeft: '4px solid #e74c3c' }}>
          <div style={styles.kpiLabel}>Outstanding Receivables</div>
          <div style={{ ...styles.kpiValue, color: '#e74c3c' }}>
            {formatLakh(kpis.outstanding_receivables)}
          </div>
          <div style={styles.kpiSub}>
            {kpis.customers_with_dues || 0} customers
          </div>
        </div>
      </div>

      {/* ─── Secondary Row: Payables + Production this week ─── */}
      <div style={styles.twoCol}>

        {/* Alerts Panel */}
        <div style={styles.panel}>
          <div style={styles.panelHeader}>
            <h3 style={styles.panelTitle}>▲ Alerts</h3>
            <span style={styles.panelCount}>
              {alerts.length} active
            </span>
          </div>
          <div style={styles.panelBody}>
            {alerts.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>✅</div>
                <div>No alerts — all stock healthy</div>
              </div>
            ) : (
              alerts.map((alert) => (
                <div
                  key={alert.id}
                  style={{
                    ...styles.alertItem,
                    borderLeft: `4px solid ${alert.alert_type === 'low' ? '#e74c3c' : '#f39c12'}`,
                    background: alert.alert_type === 'low' ? '#fdecea' : '#fff8e1',
                  }}
                >
                  <div style={styles.alertTitle}>
                    {alert.alert_type === 'low' ? 'Low stock' : 'Expiring soon'} — {alert.product_name}
                  </div>
                  <div style={styles.alertDetail}>
                    {alert.alert_type === 'low' ? (
                      <>
                        Only {formatQty(alert.qty_available)} {alert.product_unit} left
                        {alert.min_stock && ` (min ${formatQty(alert.min_stock)} ${alert.product_unit})`}
                      </>
                    ) : (
                      <>
                        Batch {alert.batch_no} expires in {alert.days_until_expiry} day{alert.days_until_expiry !== 1 ? 's' : ''}
                      </>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Production This Week */}
        <div style={styles.panel}>
          <div style={styles.panelHeader}>
            <h3 style={styles.panelTitle}>⚗ Production this week</h3>
            <span style={styles.panelCount}>
              {productionThisWeek.reduce((sum, p) => sum + (parseInt(p.batch_count) || 0), 0)} batches
            </span>
          </div>
          <div style={styles.panelBody}>
            {productionThisWeek.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>📭</div>
                <div>No production runs this week</div>
              </div>
            ) : (
              productionThisWeek.map((p, idx) => (
                <div key={idx} style={styles.productionRow}>
                  <span style={{ fontSize: '14px', color: '#2c3e50' }}>{p.product_name}</span>
                  <span style={{ fontSize: '14px', fontWeight: '700', color: '#2c3e50' }}>
                    {formatQty(p.total_output)} {p.product_unit}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* ─── Recent Activity Table ─────────────────── */}
      <div style={{ ...styles.panel, marginTop: '24px' }}>
        <div style={styles.panelHeader}>
          <h3 style={styles.panelTitle}>◷ Recent activity</h3>
          <span style={styles.panelCount}>Last {recentActivity.length} events</span>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.headerRow}>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Detail</th>
                <th style={styles.th}>Party</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {recentActivity.length === 0 ? (
                <tr>
                  <td colSpan="5" style={styles.noData}>No recent activity</td>
                </tr>
              ) : (
                recentActivity.map((a, idx) => (
                  <tr key={`${a.activity_type}-${a.ref_id}-${idx}`} style={styles.row}>
                    <td style={styles.td}>{formatDateTime(a.sort_at || a.activity_date)}</td>
                    <td style={styles.td}>
                      <span style={getActivityBadgeStyle(a.activity_type)}>
                        {formatActivityType(a.activity_type)}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {a.activity_type === 'production'
                        ? `Batch ${a.ref_no} — ${a.detail || ''}`
                        : a.ref_no
                          ? `${a.ref_no}${a.detail ? ` — ${a.detail}` : ''}`
                          : (a.detail || '-')}
                    </td>
                    <td style={styles.td}>{a.party_name || '—'}</td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: '600' }}>
                      {a.activity_type === 'production'
                        ? `${formatQty(a.amount)}`
                        : `Rs ${formatNumber(a.amount)}`}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ─── Footer: extra info ─────────────────────── */}
      <div style={styles.footerInfo}>
        <div style={styles.footerItem}>
          <span style={styles.footerLabel}>Outstanding payables:</span>
          <strong style={{ color: '#e74c3c' }}>{formatLakh(kpis.outstanding_payables)}</strong>
          <span style={styles.footerSub}>({kpis.vendors_with_dues || 0} vendors)</span>
        </div>
        {data?.top_customer && (
          <div style={styles.footerItem}>
            <span style={styles.footerLabel}>Top customer this month:</span>
            <strong style={{ color: '#27ae60' }}>{data.top_customer.name}</strong>
            <span style={styles.footerSub}>
              ({formatLakh(data.top_customer.total_sales)} · {data.top_customer.invoice_count} invoices)
            </span>
          </div>
        )}
      </div>

    </div>
  );
};

// ─── Helpers ──────────────────────────────────────────────────

const formatNumber = (n) => {
  const num = parseFloat(n) || 0;
  return Math.round(num).toLocaleString('en-PK');
};

const formatQty = (n) => {
  const num = parseFloat(n) || 0;
  // Show 1 decimal if not a whole number, otherwise as integer
  return num % 1 === 0 ? num.toLocaleString('en-PK') : num.toFixed(1);
};

// Format as Pakistani Lakhs (1 Lakh = 100,000)
const formatLakh = (n) => {
  const num = parseFloat(n) || 0;
  if (num >= 100000) {
    return `Rs ${(num / 100000).toFixed(1)}L`;
  }
  return `Rs ${formatNumber(num)}`;
};

const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  const d = new Date(dateString);
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true
  });
};

const formatActivityType = (type) => {
  const map = {
    sale: 'Sale',
    purchase: 'Purchase',
    production: 'Production',
    receipt: 'Receipt',
    payment: 'Payment',
  };
  return map[type] || (type ? type.charAt(0).toUpperCase() + type.slice(1) : '-');
};

const getActivityBadgeStyle = (type) => {
  const palette = {
    sale:       { bg: '#d4edda', fg: '#155724' },
    purchase:   { bg: '#fff3cd', fg: '#856404' },
    production: { bg: '#cce5ff', fg: '#004085' },
    receipt:    { bg: '#d1ecf1', fg: '#0c5460' },
    payment:    { bg: '#f8d7da', fg: '#721c24' },
  };
  const c = palette[type] || { bg: '#e2e3e5', fg: '#383d41' };
  return {
    background: c.bg,
    color: c.fg,
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
    display: 'inline-block',
  };
};

// ─── Styles ───────────────────────────────────────────────────

const styles = {
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '20px',
    marginBottom: '24px',
  },
  kpiCard: {
    background: 'white',
    padding: '20px 24px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  },
  kpiLabel: {
    fontSize: '13px',
    color: '#7f8c8d',
    marginBottom: '8px',
    fontWeight: '600',
  },
  kpiValue: {
    fontSize: '28px',
    fontWeight: '700',
    marginBottom: '6px',
  },
  kpiSub: {
    fontSize: '12px',
    color: '#95a5a6',
  },
  twoCol: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))',
    gap: '20px',
  },
  panel: {
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    overflow: 'hidden',
  },
  panelHeader: {
    padding: '16px 20px',
    borderBottom: '2px solid #f0f0f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  panelTitle: {
    margin: 0,
    fontSize: '16px',
    fontWeight: '700',
    color: '#2c3e50',
  },
  panelCount: {
    fontSize: '13px',
    color: '#7f8c8d',
    fontWeight: '600',
  },
  panelBody: {
    padding: '12px 20px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  alertItem: {
    padding: '12px 14px',
    borderRadius: '8px',
  },
  alertTitle: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#2c3e50',
    marginBottom: '2px',
  },
  alertDetail: {
    fontSize: '13px',
    color: '#555',
  },
  productionRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 0',
    borderBottom: '1px solid #f0f0f0',
  },
  emptyState: {
    textAlign: 'center',
    color: '#95a5a6',
    padding: '30px 20px',
    fontSize: '14px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  headerRow: {
    background: '#f8f9fa',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: '700',
    color: '#2c3e50',
    borderBottom: '2px solid #e0e0e0',
  },
  row: {
    borderBottom: '1px solid #f0f0f0',
  },
  td: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#2c3e50',
  },
  noData: {
    padding: '40px 20px',
    textAlign: 'center',
    color: '#95a5a6',
    fontSize: '14px',
  },
  footerInfo: {
    marginTop: '24px',
    padding: '16px 20px',
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '32px',
  },
  footerItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
  },
  footerLabel: {
    color: '#7f8c8d',
  },
  footerSub: {
    fontSize: '12px',
    color: '#95a5a6',
  },
  retryButton: {
    padding: '10px 24px',
    background: '#3498db',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
  },
};

export default DashboardTab;